import { useCallback } from 'react';
import { ONE_DAY } from '@/app/constants';
import { safeStorage } from '@/app/utils/client';

const DOWNLOAD_KEY = 'VIDU_DOWNLOAD_TIME';
const DAILY_SECOND_TASK_KEY = 'VIDU_DAILY_SECOND_TASK';

/** 获取自然日字符串 'YYYY-MM-DD'（本地时区） */
export const getTodayDate = () => new Date().toLocaleDateString('en-CA');

interface DailySecondTaskData {
  /** 自然日 'YYYY-MM-DD' */
  date: string;
  /** 当日发送任务次数 */
  count: number;
  /** 当日是否已弹过窗 */
  alerted: boolean;
}

// 获取或初始化「当日第二次发送任务」数据，跨自然日自动重置
const getDailySecondTaskData = (): DailySecondTaskData => {
  const today = getTodayDate();
  const storedData = safeStorage.getItem(DAILY_SECOND_TASK_KEY);
  if (storedData) {
    try {
      const data: DailySecondTaskData = JSON.parse(storedData);
      if (data.date === today) return data;
    } catch {
      // localStorage 数据被外部写坏（非 JSON）时降级为初始数据，避免 recordTask 抛错中断任务提交回调
    }
  }
  return { date: today, count: 0, alerted: false };
};

/**
 * 记录免费用户「当日发送任务」次数，当日第二次发送时触发一次提醒回调（每自然日仅一次）
 * @param onAlert 触发提醒的回调（例如打开订阅弹窗）
 * @returns recordTask：每次发送任务成功后调用
 */
export const useDailySecondTaskAlert = (onAlert: () => void) => {
  const recordTask = useCallback(() => {
    const data = getDailySecondTaskData();
    // 当日已弹过窗后无需再计数，避免 count 无上限增长与无意义的重复写入
    if (data.alerted) return;

    const newData: DailySecondTaskData = { ...data, count: data.count + 1 };
    if (newData.count === 2) {
      newData.alerted = true;
      safeStorage.setItem(DAILY_SECOND_TASK_KEY, JSON.stringify(newData));
      onAlert();
    } else {
      safeStorage.setItem(DAILY_SECOND_TASK_KEY, JSON.stringify(newData));
    }
  }, [onAlert]);

  return { recordTask };
};

// 下载提醒 hook
export const useDownloadAlertPricing = () => {
  const getIsAlertPricing = () => {
    const lastAlertTime = safeStorage.getItem(DOWNLOAD_KEY);
    return !lastAlertTime || Date.now() - Number(lastAlertTime) >= ONE_DAY;
  };

  const markAsShown = () => {
    safeStorage.setItem(DOWNLOAD_KEY, Date.now().toString());
  };

  return {
    getIsAlertPricing,
    markAsShown,
  };
};
